import { useMemo, useEffect, useState } from "react";
import { Outlet, useNavigate } from "fusion-plugin-react-router";
import { Helmet } from "fusion-plugin-react-helmet-async";
import { assetUrl } from "fusion-core";
import { assetsPath } from "../../../../shared/utils/assetsPath";
import {
  getUserIndicator,
  getCustomerObjectId,
  getForeName,
} from "../../../../shared/utils/userSessionUtils";
import Footer from "../../../../shared/components/footer";
import LandingNavbar from "../../components/dashboard/LandingNavbar";
import LandingSidebar from "../../components/dashboard/LandingSidebar";
import {
  getCustomerPolicies,
  getCustomerQuotes,
} from "../../../../shared/services/insuranceService";
import Loader from "../../../../shared/components/Loader";
import useAppStateStore from "../../../../shared/store/AppStateStore";
import {
  clearAllStores,
  clearSupportStore,
} from "../../../../shared/utils/storeUtils";

function LandingLayout() {
  let STATE = useAppStateStore();
  const navigate = useNavigate();
  const indicator = getUserIndicator();
  const customerObjectId = getCustomerObjectId();
  const foreName = getForeName();

  // State for policies, quotes, and loading
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true;
    clearSupportStore();
    clearAllStores();
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        if (indicator === "Policy") {
          const policies = await getCustomerPolicies(customerObjectId);
          if (isMounted) STATE.setCustomerPolicies(policies);
        } else if (indicator === "Quote") {
          const quotes = await getCustomerQuotes(customerObjectId);
          if (isMounted) STATE.setCustomerQuotes(quotes);
        } else {
          STATE.setCustomerPolicies(null);
          STATE.setCustomerQuotes(null);
        }
      } catch (err) {
        if (isMounted) setError(err.message || "Failed to fetch data");
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    fetchData();
    return () => {
      isMounted = false;
    };
  }, [indicator, customerObjectId]);

  // Sidebar menu logic
  const menuItems = useMemo(() => {
    const base = [{ name: "Home", icon: assetsPath.heart, route: "/landing" }];
    if (indicator === "Policy") {
      base.push(
        {
          name: "Report a Claim",
          icon: assetsPath.heart,
          action: () => navigate("/claim"),
        },
        {
          name: "Make a Change",
          icon: assetsPath.heart,
          action: () => navigate("/support?supportType=changePolicy"),
        },
        {
          name: "Cancel Policy",
          icon: assetsPath.heart,
          action: () => navigate("/support?supportType=cancelPolicy"),
        }
      );
    }
    base.push({
      name: "Support",
      icon: assetsPath.heart,
      route: "/landing/contact",
    });
    return base;
  }, [indicator, navigate]);

  // Pass dashboard data as context to Outlet
  const outletContext = {
    indicator,
    foreName,
    error,
  };

  return (
    <>
      <Helmet>
        <link
          rel="stylesheet"
          href={assetUrl("../../../../styles/landing.css")}
        />
      </Helmet>
      <LandingNavbar menuItems={menuItems} />
      <div className="portal-container">
        <LandingSidebar menuItems={menuItems} />
        <main className="portal-content">
          <Outlet context={outletContext} />
        </main>
      </div>
      <Footer />
      {loading && <Loader text="Loading..." />}
    </>
  );
}

export default LandingLayout;
